import type { SuperTicketTemplateSettings } from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { getSuperTicketSectionOverflow } from '@/lib/super-ticket-overflow'

type OverflowSections = ReturnType<typeof getSuperTicketSectionOverflow>

type SuperTicketPreviewProps = {
  html: string
  page: SuperTicketTemplateSettings['page']
  selectedSection?: string | null
  sections: SuperTicketTemplateSettings['sections']
  onSelectSection?: (section: string) => void
  onOverflowChange?: (sections: OverflowSections, html: string) => void
}

export function SuperTicketPreview({
  html,
  page,
  selectedSection,
  sections,
  onSelectSection,
  onOverflowChange,
}: SuperTicketPreviewProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [size, setSize] = useState({ width: 700, height: 640 })
  const [visibleHtml, setVisibleHtml] = useState(html)
  const pageWidth = page.width * 96
  const pageHeight = page.height * 96
  const scale = Math.min(size.width / pageWidth, size.height / pageHeight, 1)
  // Fit the physical page to the available width and the current screen height.
  useEffect(() => {
    const container = containerRef.current
    if (!container) return
    const resize = () =>
      setSize({
        width: Math.max(container.clientWidth, 1),
        height: Math.max(240, Math.min(640, window.innerHeight - 288)),
      })
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resize)
    observer?.observe(container)
    window.addEventListener('resize', resize)
    resize()
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', resize)
    }
  }, [])

  // Preserve the loaded iframe while its replacement renders. Stable keys let the
  // ready frame become visible without remounting its document a second time.
  const frames = visibleHtml === html ? [html] : [visibleHtml, html]
  return (
    <div
      ref={containerRef}
      className="min-w-0 overflow-auto text-action"
      data-testid="super-ticket-template-preview-frame"
    >
      <div
        className="relative mx-auto overflow-hidden bg-white shadow-sm"
        style={{ width: pageWidth * scale, height: pageHeight * scale }}
      >
        {frames.map((frameHtml) => (
          <PreviewFrame
            key={frameHtml}
            html={frameHtml}
            visible={frameHtml === visibleHtml}
            pageWidth={pageWidth}
            pageHeight={pageHeight}
            scale={scale}
            selectedSection={selectedSection}
            sections={sections}
            onSelectSection={onSelectSection}
            onReady={(overflow) => {
              if (frameHtml !== html) return
              setVisibleHtml(frameHtml)
              onOverflowChange?.(overflow, frameHtml)
            }}
          />
        ))}
      </div>
    </div>
  )
}

function PreviewFrame({
  html,
  visible,
  pageWidth,
  pageHeight,
  scale,
  selectedSection,
  sections,
  onSelectSection,
  onReady,
}: Omit<SuperTicketPreviewProps, 'page' | 'onOverflowChange'> & {
  visible: boolean
  pageWidth: number
  pageHeight: number
  scale: number
  onReady: (sections: OverflowSections) => void
}) {
  const frameRef = useRef<HTMLIFrameElement | null>(null)
  const [documentLoaded, setDocumentLoaded] = useState(false)
  const highlight = useEffectEvent(() => {
    const frame = frameRef.current
    const document = frame?.contentDocument
    const container = frame?.parentElement?.parentElement
    for (const section of document?.querySelectorAll<HTMLElement>('[data-section]') ?? []) {
      section.style.outline =
        section.dataset.section === selectedSection && container
          ? `2px solid ${getComputedStyle(container).color}`
          : ''
      section.title = sections[String(section.dataset.section)]?.label ?? ''
      section.style.outlineOffset = '-2px'
      section.style.cursor = onSelectSection ? 'pointer' : ''
    }
  })
  // Selection outlines exist only in the preview, never in the printed HTML.
  useEffect(() => {
    highlight()
  }, [selectedSection])
  const selectSection = useEffectEvent((section: string) => onSelectSection?.(section))
  const ready = useEffectEvent((document: Document) => {
    highlight()
    onReady(getSuperTicketSectionOverflow(document, Object.keys(sections)))
  })
  // React captures even an immediately loaded srcDoc. Synchronize with the
  // document after that event, and cancel any pending swap when it is replaced.
  useEffect(() => {
    if (!documentLoaded) return
    const document = frameRef.current?.contentDocument
    if (!document) return
    let cancelled = false
    let paint: number | undefined
    const clicked = (click: MouseEvent) => {
      const ElementClass = document.defaultView?.Element
      if (!ElementClass || !(click.target instanceof ElementClass)) return
      const section = click.target.closest('[data-section]')
      const key = section?.getAttribute('data-section')
      if (key !== undefined && key !== null && sections[key]) selectSection(key)
    }
    document.addEventListener('click', clicked)
    void Promise.resolve(document.fonts?.ready).then(() => {
      if (cancelled) return
      paint = requestAnimationFrame(() => {
        if (!cancelled) ready(document)
      })
    })
    return () => {
      cancelled = true
      if (paint !== undefined) cancelAnimationFrame(paint)
      document.removeEventListener('click', clicked)
    }
  }, [documentLoaded, sections])

  return (
    <iframe
      ref={frameRef}
      title={visible ? 'Super ticket preview' : 'Preparing super ticket preview'}
      aria-hidden={!visible}
      tabIndex={visible ? undefined : -1}
      sandbox="allow-same-origin"
      srcDoc={html}
      onLoad={() => setDocumentLoaded(true)}
      className="absolute left-0 top-0 block border-0 bg-white"
      style={{
        width: pageWidth,
        height: pageHeight,
        transform: `scale(${scale})`,
        transformOrigin: 'top left',
        visibility: visible ? 'visible' : 'hidden',
      }}
    />
  )
}
